// FLEET LIVE's arrangement (#6779): which panels show (`fleet-live-vis.ts`), where they sit
// (`fleet-live-layout.ts`), the one "Place in" menu open, the grip drag (`fleet-live-drag.ts`),
// the sound switch (`fleet-live-sound.ts`), and the polite announcements. Every one is a
// VIEWER PREFERENCE, shown to every role, stored per browser through `local-pref.ts`.
// `FleetLiveSection` renders it.
//
// The panels are rendered once and placed into their zones (`fleet-live-zones.ts`), so a
// move or a reset never re-mounts one. After a move by the keyboard path, focus returns to
// the moved panel's "Place" button, since moving its wrap in the DOM drops the focus.

import { createEffect, createSignal, on, onCleanup } from 'solid-js';
import { t } from '../i18n';
import { createPanelDrag, type PanelDrag } from './fleet-live-drag';
import {
  LIVE_ZONES,
  type LiveLayout,
  type LiveZone,
  movePanel,
  panelsIn,
  readLayout,
  resetLayout,
  writeLayout,
} from './fleet-live-layout';
import type { LiveLoop } from './fleet-live-loop';
import { type FleetCue, fleetSoundOn, playFleetCue, writeFleetSound } from './fleet-live-sound';
import {
  FLEET_LIVE_PANELS,
  type FleetLivePanel,
  type FleetLiveVis,
  readFleetLiveVis,
  writeFleetLiveVis,
} from './fleet-live-vis';
import { flicker, relayout, type WrapEls, type ZoneEls } from './fleet-live-zones';

/** "Hide all" / "Show all" staggers the panels this far apart (prototype `:4140`). */
export const STAGGER_MS = 110;
/** "Go to Core Intelligence" replays the flicker this long after it starts scrolling. */
const GOTO_FLICKER_MS = 450;

export const PANEL_NAME: Readonly<Record<FleetLivePanel, () => string>> = {
  core: () => t('fleet.live.core'),
  deck: () => t('fleet.live.deck'),
  topo: () => t('fleet.live.topology'),
};

/** The dock switches' shorter labels (prototype `PANELS[].short`). */
export const PANEL_SHORT: Readonly<Record<FleetLivePanel, () => string>> = {
  core: () => t('fleet.live.switch_core'),
  deck: () => t('fleet.live.deck'),
  topo: () => t('fleet.live.topology'),
};

export const ZONE_NAME: Readonly<Record<LiveZone, () => string>> = {
  top: () => t('fleet.live.zone_top'),
  middle: () => t('fleet.live.zone_middle'),
  side: () => t('fleet.live.zone_side'),
  bottom: () => t('fleet.live.zone_bottom'),
};

export type Arrange = {
  readonly vis: () => FleetLiveVis;
  readonly layout: () => LiveLayout;
  readonly anyShown: () => boolean;
  readonly sound: () => boolean;
  readonly announce: () => string;
  readonly menu: () => FleetLivePanel | null;
  readonly drag: PanelDrag;
  /** The zone holds a shown panel (an empty zone collapses, except while dragging). */
  readonly zoneHas: (zone: LiveZone) => boolean;
  readonly setPanel: (key: FleetLivePanel, shown: boolean) => void;
  readonly all: () => void;
  readonly toggleSound: () => void;
  readonly toggleMenu: (key: FleetLivePanel) => void;
  readonly place: (key: FleetLivePanel, zone: LiveZone) => void;
  readonly reset: () => void;
  readonly goTo: (key: FleetLivePanel) => void;
  readonly cue: (cue: FleetCue) => void;
  /** Re-read which zones hold a shown panel (a show or hide finished). */
  readonly settle: () => void;
  readonly zoneRef: (zone: LiveZone, el: HTMLElement) => void;
  readonly wrapRef: (key: FleetLivePanel, el: HTMLElement) => void;
  readonly placeRef: (key: FleetLivePanel, el: HTMLButtonElement) => void;
  readonly wrap: (key: FleetLivePanel) => HTMLElement | undefined;
  /**
   * The zones unmounted (the read went back to no body, as on an account switch): forget their
   * elements, so the next mount places its panels again rather than leaving them all in Top.
   */
  readonly unmount: () => void;
};

/** One-shot timers, all cleared when the section unmounts. */
function createLater(): (ms: number, run: () => void) => void {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  onCleanup(() => {
    for (const id of timers) clearTimeout(id);
  });
  return (ms, run) => {
    const id = setTimeout(() => {
      timers.delete(id);
      run();
    }, ms);
    timers.add(id);
  };
}

/**
 * The zones' and panels' elements, and the placement of each panel into its zone: the first
 * placement once every element is registered, and again after the zones remount.
 */
function createPlacement(layout: () => LiveLayout) {
  const [ready, setReady] = createSignal(false);
  const zones: { -readonly [Z in LiveZone]?: HTMLElement } = {};
  const wraps: { -readonly [K in FleetLivePanel]?: HTMLElement } = {};
  const places: Partial<Record<FleetLivePanel, HTMLButtonElement>> = {};
  const complete = (): void => {
    if (LIVE_ZONES.every((z) => zones[z]) && FLEET_LIVE_PANELS.every((k) => wraps[k])) {
      setReady(true);
    }
  };
  createEffect(
    on(ready, (r) => {
      if (r) relayout(layout(), zones as ZoneEls, wraps as WrapEls, false);
    }),
  );
  return {
    zones,
    wraps,
    places,
    relayout: (next: LiveLayout, motion: boolean) =>
      relayout(next, zones as ZoneEls, wraps as WrapEls, motion),
    zoneAt: (x: number, y: number): LiveZone | null => {
      for (const z of LIVE_ZONES) {
        const r = zones[z]?.getBoundingClientRect();
        if (r !== undefined && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return z;
      }
      return null;
    },
    zoneRef: (zone: LiveZone, el: HTMLElement) => {
      zones[zone] = el;
      complete();
    },
    wrapRef: (key: FleetLivePanel, el: HTMLElement) => {
      wraps[key] = el;
      complete();
    },
    unmount: () => {
      for (const z of LIVE_ZONES) delete zones[z];
      for (const k of FLEET_LIVE_PANELS) delete wraps[k];
      setReady(false);
    },
  };
}

export function createArrange(loop: LiveLoop): Arrange {
  const [vis, setVis] = createSignal(readFleetLiveVis());
  const [layout, setLayout] = createSignal(readLayout());
  const [sound, setSound] = createSignal(fleetSoundOn());
  const [announce, setAnnounce] = createSignal('');
  const [menu, setMenu] = createSignal<FleetLivePanel | null>(null);
  const [settled, setSettled] = createSignal(0);
  const placed = createPlacement(layout);
  const { wraps, places } = placed;
  const later = createLater();
  const motion = (): boolean => !loop.reducedMotion();
  const cue = (c: FleetCue): void => {
    void playFleetCue(c);
  };

  const apply = (next: LiveLayout, moved: FleetLivePanel | null): void => {
    setLayout(next);
    writeLayout(next);
    setMenu(null);
    placed.relayout(next, motion());
    cue('dock');
    loop.invalidate();
    if (moved !== null) flicker(wraps[moved], motion());
  };

  const drag = createPanelDrag({
    zoneAt: placed.zoneAt,
    onLift: () => {
      setMenu(null);
      cue('lift');
    },
    onDrop: (key, zone) => {
      if (layout().lay[key] === zone) return;
      apply(movePanel(layout(), key, zone), key);
      setAnnounce(t('fleet.live.moved', { panel: PANEL_NAME[key](), zone: ZONE_NAME[zone]() }));
    },
  });

  const setPanel = (key: FleetLivePanel, shown: boolean): void => {
    if (vis()[key] === shown) return;
    const next = { ...vis(), [key]: shown };
    setVis(next);
    writeFleetLiveVis(next);
    cue(shown ? 'on' : 'off');
    const panel = PANEL_NAME[key]();
    setAnnounce(
      shown ? t('fleet.live.panel_shown', { panel }) : t('fleet.live.panel_hidden', { panel }),
    );
  };
  const anyShown = (): boolean => FLEET_LIVE_PANELS.some((k) => vis()[k]);

  return {
    vis,
    layout,
    anyShown,
    sound,
    announce,
    menu,
    drag,
    zoneHas: (zone) => {
      settled();
      return panelsIn(layout(), zone).some((k) => vis()[k] && wraps[k]?.hidden !== true);
    },
    setPanel,
    all: () => {
      const shown = !anyShown();
      FLEET_LIVE_PANELS.forEach((key, i) => {
        if (i === 0) setPanel(key, shown);
        else later(i * STAGGER_MS, () => setPanel(key, shown));
      });
    },
    toggleSound: () => {
      const on = !sound();
      setSound(on);
      writeFleetSound(on);
      if (on) cue('dock');
    },
    toggleMenu: (key) => {
      const open = menu() === key ? null : key;
      setMenu(open);
      if (open !== null) cue('lift');
    },
    place: (key, zone) => {
      if (layout().lay[key] !== zone) {
        apply(movePanel(layout(), key, zone), key);
        setAnnounce(t('fleet.live.moved', { panel: PANEL_NAME[key](), zone: ZONE_NAME[zone]() }));
      } else setMenu(null);
      places[key]?.focus();
    },
    reset: () => {
      const open = menu();
      apply(resetLayout(), null);
      if (open !== null) places[open]?.focus();
      setAnnounce(t('fleet.live.layout_reset'));
    },
    goTo: (key) => {
      const w = wraps[key];
      if (w === undefined) return;
      w.scrollIntoView?.({ behavior: motion() ? 'smooth' : 'auto', block: 'center' });
      later(GOTO_FLICKER_MS, () => flicker(w, motion()));
    },
    cue,
    settle: () => setSettled((n) => n + 1),
    zoneRef: placed.zoneRef,
    wrapRef: placed.wrapRef,
    placeRef: (key, el) => {
      places[key] = el;
    },
    wrap: (key) => wraps[key],
    unmount: placed.unmount,
  };
}
